import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useParams } from 'react-router-dom';

/**
 * The customer's page. Reached from a link in an email, with no account.
 *
 * Written for someone standing in their shop with no internet, checking on
 * their phone whether anyone is actually coming. One answer, large, at the top.
 * Everything else is secondary and can be scrolled past.
 *
 * Deliberately outside the app shell: no navigation, no sign-in prompt, nothing
 * that suggests there is more of the system to explore.
 */

type TrackedJob = {
  reference: string;
  title: string;
  status: string;
  detail: string;
  isComplete: boolean;
  area: string | null;
  technicianFirstName: string | null;
  scheduledStart: string | null;
  reportedAt: string;
  completedAt: string | null;
  isIncident: boolean;
  canRate: boolean;
  rating: { score: number; comment: string | null } | null;
  orgName: string;
  timeline: { at: string; label: string }[];
};

const when = (iso: string): string =>
  new Date(iso).toLocaleString('en-KE', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });

export function Track() {
  const { token } = useParams<{ token: string }>();

  const { data, isLoading, isError } = useQuery<TrackedJob>({
    queryKey: ['track', token],
    queryFn: async () => {
      const response = await fetch(`/api/v1/track/${token}`);
      if (!response.ok) throw new Error('not found');
      return response.json();
    },
    retry: false,
    // Someone watching this page wants it to be current without pulling to
    // refresh, but it is a public endpoint — a minute is frequent enough.
    refetchInterval: 60_000,
  });

  if (isLoading) {
    return (
      <Shell>
        <p className="text-slate-500">Loading…</p>
      </Shell>
    );
  }

  if (isError || !data) {
    return (
      <Shell>
        <h1 className="text-xl font-semibold text-slate-900">This link is not valid</h1>
        <p className="mt-2 text-slate-600">
          It may have been mistyped, or the job may have been removed. Please call us and quote your
          reference number.
        </p>
      </Shell>
    );
  }

  return (
    <Shell orgName={data.orgName}>
      <p className="text-sm text-slate-500">Reference {data.reference}</p>

      <h1 className="mt-1 text-3xl font-bold tracking-tight text-slate-900">{data.status}</h1>
      <p className="mt-2 text-lg text-slate-700">{data.detail}</p>

      {data.technicianFirstName && !data.isComplete && (
        <p className="mt-4 rounded border border-blue-100 bg-blue-50 px-4 py-3 text-blue-900">
          <span className="font-semibold">{data.technicianFirstName}</span> is your technician.
        </p>
      )}

      {data.isIncident && (
        <p className="mt-4 rounded border border-amber-200 bg-amber-50 px-4 py-3 text-amber-900">
          This is part of a wider fault affecting several customers. You do not need to report it
          separately.
        </p>
      )}

      <dl className="mt-6 divide-y divide-slate-200 border-y border-slate-200 text-sm">
        <Row label="Reported">{when(data.reportedAt)}</Row>
        {data.area && <Row label="Area">{data.area}</Row>}
        {data.scheduledStart && !data.isComplete && (
          <Row label="Expected">{when(data.scheduledStart)}</Row>
        )}
        {data.completedAt && <Row label="Completed">{when(data.completedAt)}</Row>}
      </dl>

      {data.timeline.length > 0 && (
        <section className="mt-6">
          <h2 className="text-xs font-semibold label-mono tracking-[0.12em] text-slate-500">Progress</h2>
          <ol className="mt-3 space-y-3">
            {data.timeline.map((entry, index) => (
              <li key={`${entry.at}-${index}`} className="flex gap-3 text-sm">
                <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-blue-600" />
                <span className="text-slate-900">{entry.label}</span>
                <span className="ml-auto shrink-0 text-slate-500">{when(entry.at)}</span>
              </li>
            ))}
          </ol>
        </section>
      )}

      {(data.canRate || data.rating) && <Rating token={token ?? ''} existing={data.rating} />}

      <p className="mt-8 text-sm text-slate-500">
        This page updates on its own. If something is wrong, call us and quote{' '}
        <span className="font-medium text-slate-700">{data.reference}</span>.
      </p>
    </Shell>
  );
}

/**
 * Two buttons, not five stars.
 *
 * A customer standing in their shop will answer "was that alright?" and will
 * not rank it out of five. The two answers map to 5 and 2 so the stored scale
 * has room to become a finer control later without rescaling old data.
 *
 * The comment box only appears after a negative answer — asking everyone to
 * explain themselves is how a rating prompt gets ignored, and it is the
 * unhappy answers that are worth the words.
 */
function Rating({
  token,
  existing,
}: {
  token: string;
  existing: { score: number; comment: string | null } | null;
}) {
  const queryClient = useQueryClient();
  const [score, setScore] = useState<number | null>(existing?.score ?? null);
  const [comment, setComment] = useState(existing?.comment ?? '');
  const [done, setDone] = useState(Boolean(existing));

  const submit = useMutation({
    mutationFn: (body: { score: number; comment?: string }) =>
      fetch(`/api/v1/track/${token}/rating`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }).then((r) => {
        if (!r.ok) throw new Error('Could not save that. Please try again.');
        return r.json() as Promise<{ message: string }>;
      }),
    onSuccess: () => {
      setDone(true);
      queryClient.invalidateQueries({ queryKey: ['track', token] });
    },
  });

  if (done && !submit.isPending) {
    return (
      <section className="mt-8 rounded border border-slate-200 bg-white p-4">
        <p className="font-medium text-slate-900">
          {submit.data?.message ?? 'Thank you for rating this visit.'}
        </p>
        {score !== null && score < 4 && !comment && (
          <p className="mt-1 text-sm text-slate-600">
            If you would like to tell us what went wrong, call us and quote your reference.
          </p>
        )}
      </section>
    );
  }

  return (
    <section className="mt-8 rounded border border-slate-200 bg-white p-4">
      <h2 className="font-semibold text-slate-900">How did we do?</h2>

      <div className="mt-3 flex gap-2">
        <button
          type="button"
          className={`flex-1 rounded border px-4 py-3 font-medium ${
            score === 5
              ? 'border-blue-700 bg-blue-700 text-white'
              : 'border-slate-300 bg-white text-slate-900 hover:border-slate-400'
          }`}
          /*
           * Disabled while it sends. This was the one mutation button in the
           * console with no guard at all: it submits the moment it is pressed,
           * so a double-tap — which is what a thumb on a slow connection does —
           * sent two ratings for one visit.
           */
          disabled={submit.isPending}
          onClick={() => {
            setScore(5);
            submit.mutate({ score: 5 });
          }}
        >
          {submit.isPending && score === 5 ? 'Sending…' : 'Good'}
        </button>
        <button
          type="button"
          className={`flex-1 rounded border px-4 py-3 font-medium ${
            score === 2
              ? 'border-blue-700 bg-blue-700 text-white'
              : 'border-slate-300 bg-white text-slate-900 hover:border-slate-400'
          }`}
          onClick={() => setScore(2)}
        >
          Not good
        </button>
      </div>

      {score !== null && score < 4 && (
        <div className="mt-3">
          <label className="block text-sm text-slate-600" htmlFor="rating-comment">
            What went wrong? (optional)
          </label>
          <textarea
            id="rating-comment"
            className="mt-1 w-full rounded border border-slate-300 p-2 text-slate-900"
            rows={3}
            maxLength={1000}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
          <button
            type="button"
            className="mt-2 rounded bg-blue-700 px-4 py-2 font-medium text-white disabled:opacity-60"
            disabled={submit.isPending}
            onClick={() => submit.mutate({ score, comment: comment || undefined })}
          >
            {submit.isPending ? 'Sending…' : 'Send'}
          </button>
        </div>
      )}

      {submit.isError && (
        <p className="mt-2 text-sm text-red-700">{(submit.error as Error).message}</p>
      )}
    </section>
  );
}

function Shell({ children, orgName }: { children: React.ReactNode; orgName?: string }) {
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-blue-800 px-4 py-4">
        <p className="mx-auto max-w-lg text-lg font-bold text-white">{orgName ?? 'GoSpeedNet'}</p>
      </header>
      <main className="mx-auto max-w-lg px-4 py-8">{children}</main>
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-4 py-3">
      <dt className="text-slate-500">{label}</dt>
      <dd className="text-right font-medium text-slate-900">{children}</dd>
    </div>
  );
}
